import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicShell, { PublicCard, PublicError } from '../components/PublicShell';
import { fetchPublicFileRequest, PublicApiError, uploadToFileRequest } from '../services/publicApi';
import type { PublicFileRequest } from '../types';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

interface Job {
  file: File;
  status: 'queued' | 'uploading' | 'done' | 'error';
  loaded: number;
  error?: string;
}

export default function PublicRequestView() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [request, setRequest] = useState<PublicFileRequest | null>(null);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;
    fetchPublicFileRequest(token)
      .then(data => { setRequest(data); setPhase('ready'); })
      .catch((e: unknown) => {
        setError(e instanceof PublicApiError ? e.message : 'This request link is invalid or has closed.');
        setPhase('error');
      });
  }, [token]);

  function addFiles(files: FileList | null) {
    if (!files?.length) return;
    // Snapshot eagerly: a FileList is live, and the caller resets the input's
    // value right after, which would empty it before a lazy updater ran.
    const added = Array.from(files).map(file => ({ file, status: 'queued' as const, loaded: 0 }));
    setJobs(prev => [...prev, ...added]);
  }

  function patch(index: number, next: Partial<Job>) {
    setJobs(prev => prev.map((j, i) => (i === index ? { ...j, ...next } : j)));
  }

  // Sequential so a slow connection isn't split across parallel uploads, and so
  // a quota rejection stops the rest instead of failing each one separately.
  async function startUpload() {
    if (!token) return;
    setUploading(true);
    for (let i = 0; i < jobs.length; i++) {
      if (jobs[i].status === 'done') continue;
      patch(i, { status: 'uploading', loaded: 0, error: undefined });
      try {
        await uploadToFileRequest(token, jobs[i].file, loaded => patch(i, { loaded }));
        patch(i, { status: 'done', loaded: jobs[i].file.size });
      } catch (e) {
        patch(i, {
          status: 'error',
          error: e instanceof PublicApiError ? e.message : 'Upload failed.',
        });
      }
    }
    setUploading(false);
  }

  const pending = jobs.filter(j => j.status !== 'done');
  const doneCount = jobs.filter(j => j.status === 'done').length;
  const allDone = jobs.length > 0 && doneCount === jobs.length;

  return (
    <PublicShell>
      {phase === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 200, height: 120, borderRadius: 12, background: '#e5e7eb' }} />
          <div style={{ width: 140, height: 18, borderRadius: 6, background: '#e5e7eb' }} />
        </div>
      )}

      {phase === 'error' && <PublicError title="Request unavailable" message={error} />}

      {phase === 'ready' && request && (
        <PublicCard>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2d5cf6', marginBottom: 6 }}>
              File request
            </div>
            <div style={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e', wordBreak: 'break-word' }}>{request.title}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, lineHeight: 1.6 }}>
              Upload your files below — no account needed. Only the person who created this request can see them.
              {request.deadline && (
                <>
                  <br />
                  Due by {new Date(request.deadline).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}.
                </>
              )}
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? '#2d5cf6' : '#d1d5db'}`,
              background: dragging ? '#eef2ff' : '#fafafa',
              borderRadius: 12,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              marginBottom: jobs.length ? 16 : 0,
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e' }}>Drop files here or click to browse</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>You can add more than one file</div>
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              style={{ display: 'none' }}
            />
          </div>

          {/* Queue */}
          {jobs.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
              {jobs.map((j, i) => {
                const pct = j.file.size ? Math.min(100, Math.round((j.loaded / j.file.size) * 100)) : 0;
                return (
                  <div key={`${j.file.name}-${i}`} style={{ padding: '10px 14px', borderTop: i === 0 ? 'none' : '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {j.file.name}
                        </div>
                        <div style={{ fontSize: 12, color: j.status === 'error' ? '#dc2626' : '#6b7280' }}>
                          {j.status === 'error'
                            ? j.error
                            : j.status === 'done'
                              ? `Uploaded · ${formatBytes(j.file.size)}`
                              : j.status === 'uploading'
                                ? `${pct}% · ${formatBytes(j.file.size)}`
                                : formatBytes(j.file.size)}
                        </div>
                      </div>
                      <span style={{ fontSize: 16 }}>
                        {j.status === 'done' ? '✅' : j.status === 'error' ? '⚠️' : j.status === 'uploading' ? '⏳' : ''}
                      </span>
                      {!uploading && j.status !== 'done' && (
                        <button
                          type="button"
                          onClick={() => setJobs(prev => prev.filter((_, k) => k !== i))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16, padding: 0 }}
                          aria-label={`Remove ${j.file.name}`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {j.status === 'uploading' && (
                      <div style={{ height: 4, borderRadius: 999, background: '#e5e7eb', marginTop: 8, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: '#2d5cf6', transition: 'width 120ms linear' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {allDone ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>🎉</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>
                {doneCount} file{doneCount === 1 ? '' : 's'} sent
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                Thanks — you can close this page, or add more files above.
              </div>
            </div>
          ) : (
            jobs.length > 0 && (
              <button
                type="button"
                onClick={() => void startUpload()}
                disabled={uploading || pending.length === 0}
                style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: 'none', background: uploading ? '#9ca3af' : '#2d5cf6', color: 'white', fontSize: 14, fontWeight: 600, cursor: uploading ? 'default' : 'pointer' }}
              >
                {uploading ? 'Uploading…' : `Upload ${pending.length} file${pending.length === 1 ? '' : 's'}`}
              </button>
            )
          )}
        </PublicCard>
      )}
    </PublicShell>
  );
}
